import { STORAGE_KEYS } from '../config.js';

/* -------------------------------------------------------------------------- */
/* Access token (localStorage — persists across sessions)                     */
/* -------------------------------------------------------------------------- */

export function getAccessToken() {
  try {
    return localStorage.getItem(STORAGE_KEYS.accessToken);
  } catch {
    return null;
  }
}

export function setAccessToken(jwt) {
  try {
    localStorage.setItem(STORAGE_KEYS.accessToken, jwt);
  } catch {
    /* storage unavailable (private mode / disabled) — ignore */
  }
}

export function clearAccessToken() {
  try {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
  } catch {
    /* ignore */
  }
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}

/* -------------------------------------------------------------------------- */
/* User e-mail (localStorage — returned by /verify, displayed while signed in) */
/* -------------------------------------------------------------------------- */

export function getUserEmail() {
  try {
    return localStorage.getItem(STORAGE_KEYS.userEmail);
  } catch {
    return null;
  }
}

export function setUserEmail(email) {
  try {
    if (email) localStorage.setItem(STORAGE_KEYS.userEmail, email);
  } catch {
    /* storage unavailable (private mode / disabled) — ignore */
  }
}

export function clearUserEmail() {
  try {
    localStorage.removeItem(STORAGE_KEYS.userEmail);
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------- */
/* App version (localStorage — last version reported by /study/main)          */
/* -------------------------------------------------------------------------- */

/**
 * The app/content version from the most recent `/study/main` response. Echoed
 * back to the server on the next request so it knows which version the user is
 * running. Not session data — deliberately left untouched by `clearSession()`.
 */
export function getAppVersion() {
  try {
    return localStorage.getItem(STORAGE_KEYS.appVersion);
  } catch {
    return null;
  }
}

export function setAppVersion(version) {
  try {
    if (version) localStorage.setItem(STORAGE_KEYS.appVersion, version);
  } catch {
    /* storage unavailable (private mode / disabled) — ignore */
  }
}

/**
 * Forget the cached content version. Only account deletion does this — it is the
 * one case where nothing about the previous user may stay behind; a sign-out
 * keeps it, together with the content it describes.
 */
export function clearAppVersion() {
  try {
    localStorage.removeItem(STORAGE_KEYS.appVersion);
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------- */
/* Required update (localStorage — the gate has to outlive the network)       */
/* -------------------------------------------------------------------------- */

/**
 * The stored "this build is blocked" notice, or `null` when nothing is recorded
 * (or the value is unreadable).
 *
 * A dumb accessor on purpose: whether the record still applies is a question
 * about the running build, and `views/home.js` — which raises the gate — answers
 * it. Not session data either: `clearSession()` leaves it alone, because a
 * required update is a property of the installed app and not of who is signed in.
 */
export function getHardUpdate() {
  try {
    const record = JSON.parse(localStorage.getItem(STORAGE_KEYS.hardUpdate) || 'null');
    return record && typeof record === 'object' ? record : null;
  } catch {
    return null;
  }
}

export function setHardUpdate(record) {
  try {
    localStorage.setItem(STORAGE_KEYS.hardUpdate, JSON.stringify(record));
  } catch {
    /* storage unavailable (private mode / disabled) — ignore */
  }
}

export function clearHardUpdate() {
  try {
    localStorage.removeItem(STORAGE_KEYS.hardUpdate);
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------- */
/* Pending registration (sessionStorage — survives a reload mid-flow)         */
/* -------------------------------------------------------------------------- */

export function getPendingRegistration() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEYS.pendingRegistration) || 'null');
  } catch {
    return null;
  }
}

export function setPendingRegistration(data) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.pendingRegistration, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearPendingRegistration() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.pendingRegistration);
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------- */
/* Session teardown                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Wipe every trace of the session — access token, e-mail and any pending
 * registration. Used on logout and account deletion.
 */
export function clearSession() {
  clearAccessToken();
  clearUserEmail();
  clearPendingRegistration();
}
